import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAdminAnalytics } from '@/hooks/useAdminAnalytics'

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  const today = new Date().toISOString().split('T')[0]
  const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0]
  if (dateStr === today) return 'Hoje'
  if (dateStr === yesterday) return 'Ontem'
  return d.toLocaleDateString('pt-BR', { weekday: 'short', day: 'numeric' })
}

function formatDateLabelShort(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })
}

function BarChart({ data, maxValue }: { data: { label: string; value: number }[]; maxValue: number }) {
  return (
    <div className="space-y-2">
      {data.map((item, index) => {
        const percentage = maxValue > 0 ? (item.value / maxValue) * 100 : 0
        return (
          <div key={index} className="flex items-center gap-3">
            <span className="text-xs text-alvinegro-muted w-16 text-right shrink-0">{item.label}</span>
            <div className="flex-1 h-7 bg-alvinegro-light rounded-md overflow-hidden">
              <div
                className="h-full bg-alvinegro-black rounded-md flex items-center justify-end pr-2 transition-all duration-500"
                style={{ width: `${Math.max(percentage, 4)}%` }}
              >
                {percentage > 15 && (
                  <span className="text-xs text-white font-medium">{item.value.toLocaleString('pt-BR')}</span>
                )}
              </div>
            </div>
            {percentage <= 15 && (
              <span className="text-xs text-alvinegro-muted w-10">{item.value}</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function DailyChart({ data }: { data: { date: string; count: number }[] }) {
  const maxValue = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex items-end gap-2 h-40 md:h-48">
      {data.map((item, index) => {
        const percentage = (item.count / maxValue) * 100
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-2">
            <div className="w-full flex items-end justify-center h-28 md:h-36">
              <div
                className="w-full max-w-12 bg-alvinegro-black rounded-t-md transition-all duration-500 hover:bg-alvinegro-dark cursor-pointer relative group"
                style={{ height: `${Math.max(percentage, 4)}%` }}
              >
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-alvinegro-black text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {item.count}
                </div>
              </div>
            </div>
            <span className="text-[10px] text-alvinegro-muted text-center leading-tight">{formatDateLabel(item.date)}</span>
          </div>
        )
      })}
    </div>
  )
}

function MonthlyChart({ data }: { data: { date: string; count: number }[] }) {
  const maxValue = Math.max(...data.map((d) => d.count), 1)
  return (
    <div className="flex items-end gap-1 h-48 md:h-56">
      {data.map((item, index) => {
        const percentage = (item.count / maxValue) * 100
        return (
          <div key={index} className="flex-1 flex flex-col items-center gap-1">
            <div className="w-full flex items-end justify-center h-36 md:h-44">
              <div
                className="w-full bg-alvinegro-black rounded-t-sm transition-all duration-500 hover:bg-alvinegro-dark cursor-pointer relative group"
                style={{ height: `${Math.max(percentage, 2)}%` }}
              >
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-alvinegro-black text-white text-xs px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  {item.count}
                </div>
              </div>
            </div>
            <span className="text-[9px] text-alvinegro-muted text-center leading-tight">{formatDateLabelShort(item.date)}</span>
          </div>
        )
      })}
    </div>
  )
}

export default function AnalyticsDashboard() {
  const navigate = useNavigate()
  const { data, loading, refetch } = useAdminAnalytics()
  const [period, setPeriod] = useState<'7d' | '30d'>('7d')

  const maxViews = Math.max(...data.dailyViews.map((d) => d.count), 1)
  const changePercent = data.yesterdayViews > 0
    ? Math.round(((data.todayViews - data.yesterdayViews) / data.yesterdayViews) * 100)
    : data.todayViews > 0 ? 100 : 0
  const isPositive = changePercent >= 0

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-white rounded-xl p-5 border border-alvinegro-border animate-pulse">
              <div className="h-3 bg-alvinegro-light rounded w-20 mb-3"></div>
              <div className="h-8 bg-alvinegro-light rounded w-16"></div>
            </div>
          ))}
        </div>
        <div className="bg-white rounded-xl border border-alvinegro-border p-6 h-64 animate-pulse"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-serif font-bold text-lg text-alvinegro-black">Analytics de Audiência</h2>
          <p className="text-sm text-alvinegro-muted">Dados de acesso ao portal</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-alvinegro-border rounded-lg p-0.5">
            <button
              onClick={() => setPeriod('7d')}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                period === '7d' ? 'bg-alvinegro-black text-white' : 'text-alvinegro-dark hover:bg-alvinegro-light'
              }`}
            >
              7 dias
            </button>
            <button
              onClick={() => setPeriod('30d')}
              className={`px-3 py-1.5 text-xs rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                period === '30d' ? 'bg-alvinegro-black text-white' : 'text-alvinegro-dark hover:bg-alvinegro-light'
              }`}
            >
              30 dias
            </button>
          </div>
          <button
            onClick={() => refetch()}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-alvinegro-border hover:bg-alvinegro-light transition-colors cursor-pointer"
            title="Atualizar"
          >
            <i className="ri-refresh-line text-sm text-alvinegro-dark"></i>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 border border-alvinegro-border">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-eye-line text-alvinegro-muted text-sm"></i>
            <span className="text-xs text-alvinegro-muted">Views hoje</span>
          </div>
          <div className="flex items-end gap-2">
            <span className="text-2xl font-bold text-alvinegro-black">{data.todayViews.toLocaleString('pt-BR')}</span>
            <span className={`text-xs font-medium mb-1 ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
              {isPositive ? '+' : ''}{changePercent}%
            </span>
          </div>
          <p className="text-xs text-alvinegro-muted mt-1">vs ontem ({data.yesterdayViews})</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-alvinegro-border">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-user-line text-alvinegro-muted text-sm"></i>
            <span className="text-xs text-alvinegro-muted">Sessões únicas</span>
          </div>
          <span className="text-2xl font-bold text-alvinegro-black">{data.uniqueSessionsToday.toLocaleString('pt-BR')}</span>
          <p className="text-xs text-alvinegro-muted mt-1">Visitantes hoje</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-alvinegro-border">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-calendar-line text-alvinegro-muted text-sm"></i>
            <span className="text-xs text-alvinegro-muted">Últimos 7 dias</span>
          </div>
          <span className="text-2xl font-bold text-alvinegro-black">{data.weekViews.toLocaleString('pt-BR')}</span>
          <p className="text-xs text-alvinegro-muted mt-1">Total de views</p>
        </div>

        <div className="bg-white rounded-xl p-5 border border-alvinegro-border">
          <div className="flex items-center gap-2 mb-1">
            <i className="ri-bar-chart-line text-alvinegro-muted text-sm"></i>
            <span className="text-xs text-alvinegro-muted">Últimos 30 dias</span>
          </div>
          <span className="text-2xl font-bold text-alvinegro-black">{data.monthViews.toLocaleString('pt-BR')}</span>
          <p className="text-xs text-alvinegro-muted mt-1">Total de views</p>
        </div>
      </div>

      {/* Monthly Evolution Chart - Full Width */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-semibold text-alvinegro-black text-sm">Evolução Mensal</h3>
          <span className="text-xs text-alvinegro-muted">Últimos 30 dias</span>
        </div>
        <MonthlyChart data={data.monthlyViews} />
        <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-alvinegro-border">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-alvinegro-black"></div>
            <span className="text-xs text-alvinegro-muted">Views por dia</span>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-white rounded-xl border border-alvinegro-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-alvinegro-black text-sm">Views por dia</h3>
            <span className="text-xs text-alvinegro-muted">Últimos 7 dias</span>
          </div>
          <DailyChart data={data.dailyViews} />
          <div className="flex items-center justify-center gap-4 mt-4 pt-4 border-t border-alvinegro-border">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm bg-alvinegro-black"></div>
              <span className="text-xs text-alvinegro-muted">Views</span>
            </div>
          </div>
        </div>

        {/* Top Pages */}
        <div className="bg-white rounded-xl border border-alvinegro-border p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-semibold text-alvinegro-black text-sm">Páginas mais visitadas</h3>
            <span className="text-xs text-alvinegro-muted">Últimos 7 dias</span>
          </div>
          <BarChart
            data={data.topPages.map((p) => ({
              label: p.page_path.length > 12 ? p.page_path.substring(0, 12) + '...' : p.page_path,
              value: p.count,
            }))}
            maxValue={Math.max(...data.topPages.map((p) => p.count), 1)}
          />
          {data.topPages.length === 0 && (
            <div className="text-center py-8 text-sm text-alvinegro-muted">
              <i className="ri-bar-chart-box-line text-2xl mb-2 block opacity-50"></i>
              Sem dados ainda
            </div>
          )}
        </div>
      </div>

      {/* Top News */}
      <div className="bg-white rounded-xl border border-alvinegro-border p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-alvinegro-black text-sm">Notícias mais lidas</h3>
          <span className="text-xs text-alvinegro-muted">Total acumulado</span>
        </div>
        <div className="space-y-2">
          {data.topNews.slice(0, 10).map((news, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 rounded-lg hover:bg-alvinegro-light transition-colors cursor-pointer"
              onClick={() => navigate(`/noticias/${news.slug}`)}
            >
              <span className="w-6 h-6 rounded-full bg-alvinegro-black text-white text-xs flex items-center justify-center shrink-0 font-medium">
                {index + 1}
              </span>
              <p className="text-sm text-alvinegro-dark flex-1 min-w-0 truncate">{news.title}</p>
              <div className="flex items-center gap-1 text-xs text-alvinegro-muted shrink-0">
                <i className="ri-eye-line"></i>
                {news.views.toLocaleString('pt-BR')}
              </div>
            </div>
          ))}
          {data.topNews.length === 0 && (
            <div className="text-center py-8 text-sm text-alvinegro-muted">
              <i className="ri-article-line text-2xl mb-2 block opacity-50"></i>
              Sem dados ainda
            </div>
          )}
        </div>
      </div>
    </div>
  )
}